<template>
  <ValidationObserver
    v-slot="{ handleSubmit }"
    ref="fieldModal"
  >
    <b-modal
      id="configureFieldModal"
      v-model="tableFieldModal.show"
      title="Configure Field"
      :hide-footer="loading"
      size="lg"
      scrollable
      @ok="handleSubmit(onSubmit)"
      @show="onShow"
      @hidden="hideFieldBuilderModal"
    >
      <content-spinner :show="loading || !tablesLoadingState" />
      <b-form
        v-if="!loading && tablesLoadingState"
        @submit.prevent="handleSubmit(onSubmit)"
      >
        <!--        @reset="handleSubmit(onReset)"-->
        <div class="row">
          <div class="col">
            <b-form-group>
              <template #label>
                Field Label<span class="text-danger">&nbsp;*</span>
              </template>
              <b-input
                id="configureFieldLabel"
                v-model="tableField.displayName"
                placeholder="Field Label"
                :disabled="isDisplayNameDisabled()"
                @input="onLabelChange"
              />
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Field Type">
              <field-type-select
                :id="'configureFieldType'"
                :field="tableField"
                :options="getOptions"
                :disabled="disableType"
                @change="onTypeChange"
               />
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <b-form-group label="Field Description">
              <b-textarea
                id="configureFieldDescription"
                v-model="tableField.description"
                placeholder="Field Description"
                @input="onLabelChange"
              />
            </b-form-group>
          </div>
          <div
            v-if="tableField.dataTypeJSON"
            class="col-12"
          >
            <b-form-group label="Column Alignment">
              <b-form-select
                id="configureFieldColumnAlignment"
                v-model="tableField.columnAlignment"
                :options="alignmentOptions"
                value-field="value"
                text-field="text"
              />
            </b-form-group>
          </div>
        </div>
        <div
          v-if="tableField.type === 'select'"
          class="mb-3"
        >
          <b-button
            id="selectOptions"
            variant="link"
            size="lg"
            class="btn-options-collapse"
            :class="optionsCollapse ? null : 'collapsed'"
            @click="optionsCollapse = !optionsCollapse"
          >
            Select Options
            <font-awesome-icon :icon="'chevron-down'" />
          </b-button>
          <b-collapse
            v-model="optionsCollapse"
            class="mt-2"
          >
            <b-card class="options-card">
              <div
                v-if="tableField.data.options && tableField.data.options.length"
                class="options-wrapper"
              >
                <div
                  v-for="option in tableField.data.options"
                  class="row mb-2"
                >
                  <div class="col">
                    <b-form-group
                      label-cols-sm="3"
                      label="Text: "
                      class="mb-0"
                    >
                      <b-form-input
                        v-model="option.text"
                        placeholder="Text"
                        value="option"
                        class="mr-2"
                        @input="onSelectTextChange($event, option)"
                      />
                    </b-form-group>
                  </div>
                  <div class="col">
                    <b-form-group
                      label-cols-sm="3"
                      label="Value: "
                      class="mb-0"
                    >
                      <b-form-input
                        v-model="option.value"
                        placeholder="Value"
                        value="option"
                      />
                    </b-form-group>
                  </div>
                  <b-button
                    variant="link"
                    pill
                    class="text-danger btn-delete-option"
                    @click="deleteOption(option)"
                  >
                    <font-awesome-icon :icon="'trash'" />
                  </b-button>
                </div>
              </div>
              <div
                v-else
                class="text-center text-muted"
              >
                Click "Add Option" to add options to select
              </div>
              <p class="text-right add-option-wrapper mb-0">
                <b-button
                  variant="link"
                  @click="addOption"
                >
                  Add Option
                </b-button>
              </p>
            </b-card>
          </b-collapse>
        </div>
        <b-form-group>
          <b-form-checkbox
            id="configureFieldDisplayInTable"
            v-model="tableField.displayInTable"
          >
            Display in Table
          </b-form-checkbox>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox
            id="configureFieldDisplayInForm"
            v-model="tableField.displayInForm"
          >
            Display in Form
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="fieldOptionAllowed_Required">
          <b-form-checkbox
            id="configureFieldRequired"
            v-model="tableField.isRequired"
          >
            Required
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="isDecimalField">
          <b-form-checkbox v-model="tableField.dataTypeJSON.IsPercentage">
            Is Percentage
          </b-form-checkbox>
        </b-form-group>
        <template v-if="isTextField">
          <b-form-group v-if="fieldOptionAllowed_PredictiveSearch">
            <b-form-checkbox
              id="configureFieldPredictiveSearch"
              v-model="tableField.dataTypeJSON.UsePredictiveSearch"
            >
              Predictive Search
            </b-form-checkbox>
          </b-form-group>
          <b-form-group>
            <b-form-checkbox
              id="configureFieldIsLineBreak"
              v-model="tableField.dataTypeJSON.IsLineBreak"
            >
              Line Break
            </b-form-checkbox>
          </b-form-group>
          <b-form-group v-if="tableField.isBusinessKey">
            <b-form-checkbox
              id="configureFieldCaseSensitive"
              v-model="tableField.dataTypeJSON.IsCaseSensitive"
            >
              Is Case Sensitive
            </b-form-checkbox>
          </b-form-group>
        </template>
        <b-form-group v-if="displayCheckbox">
          <b-form-checkbox
            id="configureFieldSetDefaultValueChecked"
            v-model="tableField.dataTypeJSON.DefaultOnInsert"
          >
            Set Default Value Checked
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="displayDefaultDate">
          <b-form-checkbox
            id="configureFieldSetDefaultDateToNow"
            v-model="tableField.dataTypeJSON.DefaultDateNow"
          >
            Set Default Date to Now
          </b-form-checkbox>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox
            id="configureFieldIsUnique"
            v-model="tableField.isUnique"
            :disabled="fieldTypeIsDocument"
          >
            Unique
          </b-form-checkbox>
        </b-form-group>
        <div v-if="tableField.dataTypeJSON">
          <div
            v-if="displayFormat"
            class="row"
          >
            <div class="col-sm-12">
              <b-form-group label="Format Type">
                <b-form-select
                  id="configureFieldFormatType"
                  v-model="tableField.dataTypeJSON.FormatString"
                  :options="getFormatOptions"
                  value-field="id"
                  text-field="text"
                />
              </b-form-group>
            </div>
          </div>
          <div
            v-if="displayMinMax"
            class="row"
          >
            <div class="col-sm-6">
              <b-form-group label="Min Value">
                <b-input
                  id="configureFieldMinValue"
                  v-model="tableField.dataTypeJSON.MinimumCharacters"
                  placeholder="Min"
                  type="number"
                />
              </b-form-group>
            </div>
            <div class="col-sm-6">
              <b-form-group label="Max Value">
                <b-input
                  id="configureFieldMaxValue"
                  v-model="tableField.dataTypeJSON.MaximumCharacters"
                  placeholder="Max"
                  type="number"
                />
              </b-form-group>
            </div>
          </div>
          <div
            v-if="displayPrecisions"
            class="row"
          >
            <div class="col-sm-4">
              <b-form-group label="Precision">
                <b-input
                  id="configureFieldPrecision"
                  v-model="tableField.dataTypeJSON.Precision"
                  :class="{ 'border-danger': precisionError }"
                  placeholder="Precision"
                  min="0"
                  max="29"
                  type="number"
                  :title="precisionTooltip"
                  @input="validatePrecision"
                  @mouseover="showPrecisionTooltip"
                />
              </b-form-group>
            </div>
            <div class="col-sm-4">
              <b-form-group label="Scale">
                <b-input
                  id="configureFieldScale"
                  v-model="tableField.dataTypeJSON.Scale"
                  :class="{ 'border-danger': scaleError }"
                  min="0"
                  :max="tableField.dataTypeJSON.Precision - 1"
                  placeholder="Scale"
                  type="number"
                  :title="scaleTooltip"
                  @input="validateScale"
                  @mouseover="showScaleTooltip"
                />
              </b-form-group>
            </div>
            <div class="col-sm-4">
              <b-form-group label="Decimals to Display">
                <b-input
                  id="configureFieldDecimalsToDisplay"
                  v-model="tableField.dataTypeJSON.DecimalsToDisplay"
                  :class="{ 'border-danger': decimalsToDisplayError }"
                  :min="0"
                  :max="tableField.dataTypeJSON.Scale"
                  placeholder="Decimals to Display"
                  type="number"
                  :title="decimalsTooltip"
                  @input="validateDecimalsToDisplay"
                  @mouseover="showDecimalsTooltip"
                />
              </b-form-group>
            </div>
            <div class="col-sm-4">
              <b-form-group>
                <b-form-checkbox v-model="tableField.dataTypeJSON.ApplyRounding">
                  Apply Rounding
                </b-form-checkbox>
              </b-form-group>
            </div>
          </div>
          <!--
            'Lookup' or 'Parent Child' fields.
            Related Table and Column selection.
          -->
          <div v-if="fieldTypeIsLookup || fieldTypeIsParentChildHierarchy">
            <hr>
            <div class="row">
              <div :class="['col-sm-4', fieldTypeIsLookup && 'col-sm-6']">
                <b-form-group>
                  <template #label>
                    Related Table<span class="text-danger">&nbsp;*</span>
                  </template>
                  <select
                    id="configureFieldRelatedTable"
                    ref="type"
                    v-model="tableField.dataTypeJSON.RelatedEntity"
                    class="mr-sm-2 custom-select"
                    @change="onTableChange($event)"
                  >
                    <option
                      v-for="option in parseAvailableTables"
                      :id="$options.filters.displayName(option.displayName)"
                      :value="option.entityGuid"
                    >
                      {{ option.displayName }}
                    </option>
                  </select>
                </b-form-group>
                
                <b-form-group>
                  <b-form-checkbox
                    v-if="fieldTypeIsLookup"
                    id="configureFieldDisableDownstreamRowLevelSecurityLookup"
                    v-model="tableField.dataTypeJSON.DisableDownstreamRowLevelSecurity"
                  >
                    Disable Downstream RowLevel Security
                  </b-form-checkbox>
                </b-form-group>
              </div>
              <div :class="['col-sm-4',fieldTypeIsLookup && 'col-sm-6']">
                <b-form-group>
                  <template #label>
                    Related Table Column<span class="text-danger">&nbsp;*</span>
                  </template>
                  <select
                    id="configureFieldRelatedTableColumn"
                    ref="type"
                    v-model="tableField.dataTypeJSON.FieldToDisplayGuid"
                    class="mr-sm-2 custom-select"
                    @change="onColumnChange($event)"
                  >
                    <option
                      v-for="option in relatedColumnOptions"
                      :id="$options.filters.displayName(option.displayName)"
                      :value="option.fieldGuid"
                    >
                      {{ option.displayName }}
                    </option>
                  </select>
                </b-form-group>
              </div>
          <!--
            'Parent Child' fields.
            Default Value selection.
          -->
          <div
                v-if="fieldTypeIsParentChildHierarchy"
                class="col-sm-4"
              >
                <b-form-group label="Default Value">
                  <select
                    id="configureFieldRelatedColumnDefaultValue"
                    ref="type"
                    v-model="tableField.dataTypeJSON.DefaultValue"
                    class="mr-sm-2 custom-select"
                  >
                    <option
                      v-for="option in relatedColumnItems"
                      :id="$options.filters.displayName(option.displayName)"
                      :value="option.recordGuid"
                    >
                      {{ option.displayName }}
                    </option>
                  </select>
                </b-form-group>
              </div>
            </div>
          </div>
          <!-- 'Lookup' field properties -->
          <div v-if="fieldTypeIsLookup">
            <div class="lookup-configuration__wrapper">
              <b-form-group>
                <b-form-checkbox
                  v-if="fieldTypeIsLookup"
                  id="configureStrictLookupRules"
                  v-model="isStrictLookupRules"
                >
                  Enable strict lookup rules
                  <font-awesome-icon
                    :icon="['fas', 'info-circle']"
                    v-b-tooltip.hover.right="'Only allow columns that enforce required and unique values as lookups'"
                    class="ml-1"
                  />
                </b-form-checkbox>
              </b-form-group>
            </div>
            <div class="lookup-configuration__wrapper">
              <b-form-group :title="fieldHasCalculation ? 'Fuzzy Matching is not available - Field has a Calculation.' : ''">
                <b-form-checkbox
                  v-if="fieldTypeIsLookup"
                  id="configureFieldFuzzyMatchingLookup"
                  v-model="isUseFuzzyMatchingVisible"
                  :disabled="fieldHasCalculation"
                >
                  Use fuzzy matching
                </b-form-checkbox>
              </b-form-group>
            </div>

            <div v-if="isUseFuzzyMatchingVisible && tableField">
              <upper-fuzzi-matching />
              <lower-fuzzy-matching-list
                :get-related-column-options="getRelatedColumnOptions"
                :table="table"
                :field="field"
              />
            </div>
          </div>
          <!--
            'Parent Child' fields.
            Level Field selection.
            Minimum/Maximum Level entry.
            'Display As' selection (radio buttons).
          -->
          <div v-if="fieldTypeIsParentChildHierarchy">
            <div class="row">
              <div class="col-sm-4">
                <b-form-group label="Level Field">
                  <b-form-select
                    id="configureFieldRelatedField"
                    ref="type"
                    v-model="tableField.dataTypeJSON.LevelFieldGuid"
                    class="mr-sm-2"
                    :options="relatedColumnOptions"
                    value-field="fieldGuid"
                    text-field="displayName"
                  />
                </b-form-group>
              </div>
              <div class="col-sm-4">
                <b-form-group label="Min Level">
                  <b-input
                    id="configureFieldMinLevel"
                    v-model="tableField.dataTypeJSON.MinimumLevelSelectable"
                    placeholder="Min Level"
                    type="number"
                    min="0"
                    value="0"
                  />
                </b-form-group>
              </div>
              <div class="col-sm-4">
                <b-form-group label="Max Level">
                  <b-input
                    id="configureFieldMaxLevel"
                    v-model="tableField.dataTypeJSON.MaximumLevelSelectable"
                    placeholder="Max Level"
                    type="number"
                    min="0"
                  />
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12">
                <b-form-group label="Display as">
                  <b-form-radio-group
                    id="configureFieldDisplayAs"
                    v-model="tableField.dataTypeJSON.ControlType"
                    :options="displayHierarchyOptions"
                    value-field="id"
                    text-field="text"
                  />
                </b-form-group>
              </div>
            </div>
          </div>
          <!--
            'Document' fields.
            Document Type selection.
            Default SharePoint Location field with browse functionality.
            Maximum Files Per Row field.
          -->
          <div v-if="fieldTypeIsDocument">
            <hr>
            <div class="row">
              <div class="col-sm-12">
                <b-form-group>
                  <template #label>
                    Default SharePoint Location<span class="text-danger">&nbsp;*</span>
                  </template>
                  <div class="d-flex align-items-center">
                    <font-awesome-icon 
                      :icon="['fas', 'folder']" 
                      class="mr-2 text-muted"
                    />
                    <a 
                      id="configureFieldSharePointLocation"
                      href="#" 
                      class="text-primary"
                      @click.prevent="showSharePointModal = true"
                    >
                      Browse
                    </a>
                  </div>
                  <b-form-input
                    v-if="tableField.dataTypeJSON.SPDefaultSiteId && !sharePointDisplayPathError"
                    id="sharePointDisplayPath"
                    v-model="sharePointDisplayPath"
                    readonly
                    class="mt-2"
                  />
                  <div
                    v-if="sharePointDisplayPathError"
                    class="text-danger mt-1"
                  >
                    {{ sharePointDisplayPathError }}
                  </div>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6">
                <b-form-group label="Maximum Files Allowed Per Row">
                  <b-form-input
                    id="configureFieldMaxFilesPerRow"
                    v-model="tableField.dataTypeJSON.MaxFilesPerRow"
                    type="number"
                    min="1"
                    placeholder="Maximum Files Allowed Per Row"
                  />
                  <small class="form-text text-muted">
                    Optionally restrict the number of files that can be added to a single row.
                  </small>
                </b-form-group>
              </div>
            </div>
          </div>
        </div>
        <b-button
          type="submit"
          class="d-none"
        />
      </b-form>

      <div
        slot="modal-footer"
        class="d-flex"
      >
        <b-button
          id="configureFieldModalCancelBtn"
          class="mt-3 mr-3"
          block
          @click="$bvModal.hide('configureFieldModal')"
        >
          Cancel
        </b-button>
        <b-button
          id="configureFieldModalOkBtn"
          v-b-tooltip.hover
          class="mt-3 mr-3"
          variant="primary"
          block
          :disabled="isApplyButtonDisabled()"
          :title="isApplyButtonDisabled()
            ? 'Please complete all mandatory fields (marked with *) before applying changes'
            : ''
          "
          @click.prevent="onSubmit"
        >
          Apply
        </b-button>
      </div>
    </b-modal>

    <!-- SharePoint Location Modal -->
    <share-point-selector-modal
      v-if="showSharePointModal"
      :initial-location-data="tableField.dataTypeJSON"
      title="Select SharePoint Location"
      @location-selected="onSharePointLocationSelected"
      @hidden="showSharePointModal = false"
    />
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {FIELD_INITIAL_DATA, getFieldInitialData} from '@/store/modules/data-management/state';
import ContentSpinner from '../../../../../core/components/ContentSpinner';
import {v4 as uuidv4} from 'uuid';
import {cloneDeep} from 'lodash-es';
import * as entityUtils from '@/core/utils/entityUtils';
import { constructSharePointDisplayPath } from '@/core/utils/sharePointUtils';
/* Components */
import FieldTypeSelect from '../components/FieldTypeSelect.vue';
import UpperFuzziMatching from '../components/UpperFuzziMatching.vue';
import LowerFuzzyMatchingList from '@/modules/DataManagement/views/tableBuilder/components/LowerFuzzyMatchingList.vue';
import SharePointSelectorModal from '@/modules/DataManagement/components/SharePointSelectorModal.vue';

/* State data */
const {mapGetters, mapState, mapActions} = createNamespacedHelpers('dataManagement');
const {mapState: appMapState} = createNamespacedHelpers('app');


export default {
  name: 'FieldModal',
  components: {
    FieldTypeSelect,
    LowerFuzzyMatchingList,
    ContentSpinner,
    UpperFuzziMatching,
    SharePointSelectorModal,
  },
  props: {
    table: {
      default: {},
      type: Object,
    },
  },
  data() {
    return {
      ffs: '',
      optionsCollapse: false,
      /* Add Entity Utils to 'this' so that they can be referenced in v-if etc.  */
      entityUtils: entityUtils,
      /* Unclear why we have field as well as tableField (computed). */
      field: getFieldInitialData(),
      selectOptions: [],
      relatedColumnOptions: [], // List of Fields in the selected related table that can be looked up from 
      relatedColumnItems: [], 
      tableColumns: [],
      displayError: null,
      precisionError: false,
      precisionTooltip: '',
      scaleError: false,
      scaleTooltip: '',
      decimalsToDisplayError: false,
      decimalsTooltip: '',
      alignmentOptions: [
        {value: null, text: 'Select Column Alignment', disabled: true},
        {value: 'left', text: 'Left'},
        {value: 'center', text: 'Center'},
        {value: 'right', text: 'Right'},
      ],
      displayHierarchyOptions: [
        {id: 'select', text: 'Dropdown List'},
        {id: 'list', text: 'Group List'},
        {id: 'tree', text: 'Nested List'},
      ],
      dateFormatOptions: [
        {id: 'DD/MM/YYYY', text: 'DD/MM/YYYY'},
        {id: 'MM/DD/YYYY', text: 'MM/DD/YYYY'},
        // {id: 'YYYY/DD/MM', text: 'YYYY/DD/MM'},
        {id: 'YYYY/MM/DD', text: 'YYYY/MM/DD'},
        // {id: 'YYYY', text: 'YYYY'},
      ],
      wholeNumberFormatOptions: [
        {id: '', text: 'No separation'},
        {id: 'comma', text: 'Comma Separated'},
        {id: 'dot', text: 'Dot Separated'},
      ],
      fieldsOptions: [],
      loading: false,
      isUseFuzzyMatchingVisible: false,
      isLookupRelationsFilled: false,
      isStrictLookupRules: false,
      lookupRelations: [{key: uuidv4(), SourceField: '', RelatedTable: '', RelatedField: ''}],
      lookupOrderOfCalculation: 0,
      fieldConfigsArr: [],
      // formattedQuestion: '',
      question: '',
      showSharePointModal: false,
      sharePointDisplayPath: '',
      sharePointDisplayPathError: '',
    };
  },
  watch: {
    isUseFuzzyMatchingVisible(val) {
      if (val && !this.tableField.dataTypeJSON.FuzzyMatchConfig) {
        this.createFuzzyConfig();
      }
    },
    fields: {
      handler() {
        this.setInitialRelatedDropdowns();
        this.isShowLookupRelations = this.tableField.dataTypeJSON
          ? this.tableField.dataTypeJSON.IsCalculated
          : false;
        this.lookupOrderOfCalculation = (this.tableField.dataTypeJSON && this.tableField.dataTypeJSON.OrderOfCalculation)
          ? this.tableField.dataTypeJSON.OrderOfCalculation
          : 0;
        if (
          this.field.dataTypeName === this.fieldValues.lookup &&
          this.field.dataTypeJSON &&
          this.field.dataTypeJSON.SourceRelatedFieldPair &&
          this.field.dataTypeJSON.SourceRelatedFieldPair.length
        ) {
          this.lookupRelations = this.field.dataTypeJSON.SourceRelatedFieldPair
            .map(fp => {
              const relatedField = this.fields.data.find(f => f.fieldGuid === fp.RelatedField);
              return relatedField
                ? ({...fp, key: uuidv4(), RelatedTable: relatedField.entityGuid})
                : fp;
            });
        }
      },
      immediate: true,
      deep: true,
    },
    field: {
      handler(val) {
      },
      deep: true,
      immediate: true,
    },
    lookupRelations: {
      handler(newValue) {
        if (!newValue.length) {
          this.isLookupRelationsFilled = false;
        } else {
          newValue.forEach(el => {
            this.isLookupRelationsFilled = !!(el.RelatedField && el.SourceField);
          });
        }
      },
      deep: true,
      immediate: true,
    },
    tableField: {
      handler() {
        if (this.tableField) {  
          /* Clone the definition of the current Field. */
          const field = this.tableField;
          this.field = JSON.parse(JSON.stringify(field));
        } else {
          /* Initialise the Field definition. */
          this.field = getFieldInitialData();
        }
      },
      immediate: true,
      deep: true,
    },
    getOrderOfCalculation(val) {
      this.lookupOrderOfCalculation = val;
    },
    isStrictLookupRules(val) {
      const table = this.tables.data.find(t => t.entityGuid === this.tableField.dataTypeJSON.RelatedEntity);
      if (table) {
        this.relatedColumnOptions = this.getRelatedDropdowns(table.entityGuid);

        // Reset selection if selected Related Table Column is no longer valid
        const isCurrentFieldValid = this.relatedColumnOptions.some(
          option => option.fieldGuid === this.tableField.dataTypeJSON.FieldToDisplayGuid,
        );
        if (!isCurrentFieldValid) {
          this.tableField.dataTypeJSON.FieldToDisplayGuid = '';
        }
      } else { // If new lookup field being configured
        this.relatedColumnOptions = this.table.fields.filter(f => 
          f.fieldGuid !== this.field.fieldGuid &&
          (!val || (f.isRequired && f.isUnique)),
        );
      }
      this.relatedColumnOptions = this.relatedColumnOptions.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  computed: {
    ...appMapState({
      isFourthVersion: state => state.isFourthVersion,
    }),
    ...mapGetters(['fieldTypes']),
    ...mapState({
      tables: state => state.tables,
      tablesLoadingState: state => state.tables.loaded,
      fields: state => state.fields,
      numberFormatTypes: state => state.numberFormatTypes,
      getFormatOptions() {
        if (!this.tableField) {
          return [];
        } else if (this.tableField.dataTypeName === this.fieldValues.dateTime) {
          return this.dateFormatOptions.filter(f => f.id !== 'YYYY');
        } else if (this.tableField.dataTypeName === this.fieldValues.date) {
          return this.dateFormatOptions;
        } else if (this.tableField.dataTypeName === this.fieldValues.wholeNumber || this.tableField.dataTypeName === this.fieldValues.decimalNumber || this.tableField.dataTypeName === this.fieldValues.floatNumber) {
          return this.wholeNumberFormatOptions;
        }
      },
      tableFieldModal: state => state.builder.tableFieldModal,
      tableField: state => state.builder.tableFieldModal.data,
      fieldValues: state => state.fieldValues,
    }),
    parseAvailableTables() {
      const data = JSON.parse(JSON.stringify(this.tables.data));
      const tableExists = data.find((t) => t.entityGuid === this.table.entityGuid);
      if (!tableExists) {
        data.push(this.table);
      }
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
    displayFormat() {
      return [
        this.fieldValues.dateTime,
        this.fieldValues.date,
        this.fieldValues.wholeNumber,
        this.fieldValues.decimalNumber,
        this.fieldValues.floatNumber,
      ].includes(this.tableField.dataTypeName);
    },
    /** Does the Field allow the 'Required' option. */
    fieldOptionAllowed_Required() {
      return entityUtils.fieldOptionAllowed_Required(this.tableField);
    },
    /** Does the Field allow the 'Predictive Search' option. */
    fieldOptionAllowed_PredictiveSearch() {
      return entityUtils.fieldOptionAllowed_PredictiveSearch(this.tableField);
    },
    displayCheckbox() {
      return [this.fieldValues.boolean].includes(this.tableField.dataTypeName);
    },
    displayMinMax() {
      return [
        this.fieldValues.wholeNumber,
        this.fieldValues.text,
      ].includes(this.tableField.dataTypeName);
    },
    displayPrecisions() {
      return [
        this.fieldValues.decimalNumber,
        this.fieldValues.floatNumber,
      ].includes(this.tableField.dataTypeName);
    },
    /**
     * Is the Field 'Lookup' type.
     * - Previously displayRelations().
     * @returns True if the Field Type is Lookup.
     */
     fieldTypeIsLookup() {
      return entityUtils.dataTypeIsLookup(this.tableField.dataTypeName);
    },
    /**
     * Is the Field 'Parent Child Hierarchy' type.
     * - Previously displayParentChildRelation().
     * @returns True if the Field Type is 'Parent Child Hierarchy'.
     */
     fieldTypeIsParentChildHierarchy() {
      return entityUtils.dataTypeIsParentChild(this.tableField.dataTypeName);
    },
    /**
     * Is the Field 'Document' type.
     * @returns True if the Field Type is 'Document'.
     */
    fieldTypeIsDocument() {
      return entityUtils.dataTypeIsDocument(this.tableField.dataTypeName);
    },
    /* Does the field have a calculation */
    fieldHasCalculation(){
      return entityUtils.fieldHasCalculation(this.tableField);
    },
    displayDefaultDate() {
      return [
        this.fieldValues.dateTime,
        this.fieldValues.date,
      ].includes(this.tableField.dataTypeName);
    },
    isTextField() {
      return entityUtils.dataTypeIsText(this.tableField.dataTypeName);
    },
    isDecimalField() {
      return (this.tableField.dataTypeName === this.fieldValues.decimalNumber)
        || (this.tableField.dataTypeName === this.fieldValues.floatNumber);
    },
    /** Field Data Type dropdown options. */
    getOptions() {
      let fieldTypeOptions = [...this.fieldTypes];
      if (!(this.tableField.data && this.tableField.data.isPrimary)) {
        /* Exclude UUID if Field being created or not Primary. */
        fieldTypeOptions = fieldTypeOptions.filter(elem => elem.value !== 'uuid');
      }
      return fieldTypeOptions;
    },
    disableType() {
      return this.tableField && this.tableField.data && this.tableField.data.isPrimary || this.tableField && this.tableField.data && this.tableField.data.isDate;
    },
    getOrderOfCalculation() {
      if (this.tableField.dataTypeJSON && this.tableField.dataTypeJSON.OrderOfCalculation) {
        return this.field.dataTypeJSON.OrderOfCalculation;
      } else {
        const lookupFields = this.table.fields
          .filter(f => f.dataTypeName === this.fieldValues.lookup && f.fieldGuid !== this.field.fieldGuid);
        const lookupCalculatedFields = lookupFields.filter(f => f.dataTypeJSON.IsCalculated);

        return lookupCalculatedFields ? lookupCalculatedFields.length : 0;
      }
    },
 
  },
  methods: {
    uuidv4,
    ...mapActions([
      'updateTableFieldsIsCalculationState',
      'hideFieldBuilderModal',
      'getTableItems',
      'getTables',
      'getFields',
      'createFuzzyConfig',
      'transformFuzzyConfig',
      'getFieldOptions',
    ]),
    isApplyButtonDisabled() {
      if (!this.tableField.displayName) {
        return true;
      }
      if (this.tableField.dataTypeName === this.fieldValues.lookup) {    
        const isMainDataEmpty = !this.tableField.dataTypeJSON.RelatedEntity
            || !this.tableField.dataTypeJSON.FieldToDisplayGuid;   
        return this.isShowLookupRelations
          ? isMainDataEmpty || !this.isLookupRelationsFilled
          : isMainDataEmpty;
      }

      if (this.tableField.dataTypeName === this.fieldValues.document) {
        // Document field - Default SharePoint Location is required
        return !this.tableField.dataTypeJSON.SPDefaultSiteId;
      }

      if (this.tableField.dataTypeName === this.fieldValues.text) {
       /* Text field - no mandatory properties. */
       return false;
      }
      if (this.tableField.dataTypeName === this.fieldValues.decimalNumber) {
        return this.precisionError || this.scaleError || this.decimalsToDisplayError;
      }
    },
    onLabelChange(event) {
      // this.field.name = event.replace(/\s/g, '_').toLowerCase();
    },
    onSelectTextChange(event, option) {
      option.value = event.replace(/\s/g, '_').toLowerCase();
    },
    /**
     * Field's Data Type has been changed.
     * - Invoked from Data Type Select change event.
     */
    onTypeChange() {
      entityUtils.resetDataTypeProperties(this.tableField);
      if (!this.tableField.calculation)
        this.updateTableFieldsIsCalculationState(false);
    },
    addOption() {
      this.tableField.data.options = [...this.field.data.options, {text: '', value: ''}];
    },
    async deleteOption(option) {
      const success = await this.$confirm(`Deleting Option: "${option.text}"`, 'This option might be used. Are you sure you want to delete it?');
      if (success) {
        const index = this.tableField.data.options.indexOf(option);
        this.tableField.data.options.splice(index, 1);
        this.tableField.data = {...this.field.data};
      }
    },
    onSubmit() {
      if (this.tableField.dataTypeName === this.fieldValues.lookup) {
        if (!this.isUseFuzzyMatchingVisible) {
          this.tableField.dataTypeJSON.FuzzyMatchConfig = null;
        }
        if (this.isShowLookupRelations) {
          this.tableField.dataTypeJSON.SourceRelatedFieldPair =
              [...this.lookupRelations.map(rel => ({
                SourceField: rel.SourceField,
                RelatedField: rel.RelatedField,
              }),
              ),
              ];
          this.tableField.dataTypeJSON.OrderOfCalculation = Number(this.lookupOrderOfCalculation);
        } else {
          this.tableField.dataTypeJSON.SourceRelatedFieldPair = [];
        }
      }
      this.tableField.calculationJSON = this.tableField.calculation
        ? JSON.stringify(this.tableField.calculation)
        : null;

      if (this.tableField.dataTypeName === this.fieldValues.lookup) {
        delete this.tableField.dataTypeJSON.StrictLookupRules;
        this.tableField.dataTypeJSON.StrictLookupRules = this.isStrictLookupRules ? true : false;
      } else {
        delete this.tableField.dataTypeJSON.StrictLookupRules;
      }

      // Cleanup: Remove deprecated/incorrect field assignments
      delete this.tableField.strictLookupRules
      delete this.tableField.name;

      this.$emit('ok', this.tableField);
      this.hideFieldBuilderModal();
    },
    /**
     * Lookup Field - Related Table selected.
     * @param e $event.
     */
    onTableChange(e) {
      const tableUuid = e.target.value;
      this.relatedColumnItems = [];
      this.relatedColumnOptions = [];
      this.tableColumns = [];

      const table = this.tables.data.find((elem) => elem.entityGuid === tableUuid);
      if (table) {
        this.relatedColumnOptions = this.getRelatedDropdowns(table.entityGuid);
      } else { //if table is creating now
        this.relatedColumnOptions = this.table.fields.filter(f => 
          f.fieldGuid !== this.field.fieldGuid
          && (!this.isStrictLookupRules || (f.isRequired && f.isUnique)),
        );
      }
      this.tableColumns = this.relatedColumnOptions.map((elem) => elem.displayName);
      
      // RLS fields are not available for looking up to
      this.relatedColumnOptions = this.relatedColumnOptions.filter(x=>!x.isRLSField);

      
      this.relatedColumnOptions = this.relatedColumnOptions.sort((a, b) => a.name.localeCompare(b.name));

      // if (this.field.dataTypeName === this.fieldValues.lookup) {
      //   this.relatedColumnOptions = this.relatedColumnOptions.filter((f) => f.dataTypeName === this.field)
      // }
    },
    // getPrimaryKey(item) {
    //   return Object.keys(item).find(fieldName => fieldName.indexOf('Guid') > -1);
    // },
    async prepareRelatedColumnItems(relatedEntityGuid, relatedFieldName) {
      this.loading = true;
      // TODO: do not delete it'll be necessary in future
      // const relatedTableItems = await this.getTableItems(relatedEntityGuid);
      // let primaryKey = null;
      // this.relatedColumnItems = relatedTableItems.map(item => {
      //   if (!primaryKey) primaryKey = this.getPrimaryKey(item);
      //   return {
      //     recordGuid: item[primaryKey],
      //     displayName: item[relatedFieldName]
      //   };
      // }).sort((a, b) => {
      //   if (a.displayName && b.displayName) {
      //     return a.displayName.toString().localeCompare(b.displayName.toString())
      //   }
      //   return 1;
      // });
      // console.log(this.relatedColumnItems, 'this.relatedColumnItems')

      this.relatedColumnItems.unshift({
        recordGuid: null,
        displayName: '<EMPTY>',
      });

      this.loading = false;
    },
    async onColumnChange(e) {
      const fieldGuid = e.target.value;
      this.relatedColumnItems = [];

      const relatedEntityGuid = this.field.dataTypeJSON.RelatedEntity;
      const relatedFieldName = this.relatedColumnOptions.find(field => field.fieldGuid === fieldGuid).name;

      await this.prepareRelatedColumnItems(relatedEntityGuid, relatedFieldName);
    },
    async setInitialRelatedDropdowns() {
        if ((this.tableField.dataTypeName === this.fieldValues.lookup
          || this.tableField.dataTypeName === this.fieldValues.parentChildHierarchy)
          && this.fields.data.length
        ) {
          this.relatedColumnOptions = [];
          const relatedField = this.fields.data.find(elem => (
            elem.fieldGuid === this.tableField.dataTypeJSON.FieldToDisplayGuid
          ));
          if (relatedField) {
            this.relatedColumnOptions = this.getRelatedDropdowns(relatedField.entityGuid);

            const entity = this.fields.data.find(elem => (
              elem.entityGuid === relatedField.entityGuid
            ));
            this.tableField.dataTypeJSON.RelatedEntity = entity.entityGuid;
            this.prepareRelatedColumnItems(entity.entityGuid, relatedField.name);
          }
        }
      // }
    },
    async onShow() {
      //todo: creating copy (temp solution)
      this.relatedColumnOptions = [];
      this.displayError = null;

      if (this.field.dataTypeJSON.StrictLookupRules === true
       || this.field.dataTypeJSON.StrictLookupRules === false) {
        this.isStrictLookupRules = this.field.dataTypeJSON.StrictLookupRules;
      } else {
        this.isStrictLookupRules = false;
      }
      if (this.tableField.dataTypeName === 'lookup') {
        if (this.tableField.dataTypeJSON.FuzzyMatchConfig) {
          this.isUseFuzzyMatchingVisible = true;
        }
      }
    },
    getRelatedDropdowns(entityGuid) {
      return this.fields.data.filter(elem => (
        elem.entityGuid === entityGuid
        && (!this.isStrictLookupRules || (elem.isRequired && elem.isUnique))
      ));
    },
    getRelatedColumnOptions(sourceGuid) {
      const tableGuid = this.tableField.dataTypeJSON.RelatedEntity
        ? this.tableField.dataTypeJSON.RelatedEntity
        : null;

      const sourceField = this.table.fields.find(f => f.fieldGuid === sourceGuid);
      const sourceFieldType = sourceField ? sourceField.dataTypeName : '';
      const relatedFields = this.fields.data.filter(elem => elem.entityGuid === tableGuid);

      if (relatedFields && sourceFieldType && sourceFieldType === this.fieldValues.lookup) {
        return relatedFields.filter(elem => (
          elem.dataTypeName === sourceFieldType
            && JSON.parse(elem.dataTypeJSON).FieldToDisplayGuid === sourceField.dataTypeJSON.FieldToDisplayGuid
        ));
      }
      return relatedFields.filter(elem => elem.dataTypeName === sourceFieldType);
    },
    isDisplayNameDisabled() {
      return this.table.entityConfig?.MidLoadWorkflow?.Columns.includes(this.tableField.name);
    },
    validatePrecision() {
      const {Precision} = this.tableField.dataTypeJSON;
      const precision = Number(Precision);
      this.precisionError = precision < 0 || precision > 29;
      if (this.precisionError) {
        this.showPrecisionTooltip();
      } else {
        this.precisionTooltip = '';
      }
      this.validateScale();
    },
    showPrecisionTooltip() {
      const {Precision} = this.tableField.dataTypeJSON;
      const precision = Number(Precision);
      if (precision < 0) {
        this.precisionTooltip = 'Precision cannot be less than 0.';
      } else if (precision > 29) {
        this.precisionTooltip = 'Precision cannot exceed 29.';
      } else {
        this.precisionTooltip = '';
      }
    },
    validateScale() {
      const {Scale, Precision} = this.tableField.dataTypeJSON;
      const precision = Number(Precision);
      const scale = Number(Scale);

      this.scaleError = Scale < 0 || scale >= precision;
      if (this.scaleError) {
        this.showScaleTooltip();
      } else {
        this.scaleTooltip = '';
      }
      this.validateDecimalsToDisplay();
    },
    showScaleTooltip() {
      const {Scale, Precision} = this.tableField.dataTypeJSON;
      const precision = Number(Precision);
      const scale = Number(Scale);
      if (scale < 0) {
        this.scaleTooltip = 'Scale cannot be less than 0.';
      } else if (scale >= precision) {
        this.scaleTooltip = `Scale must be less than Precision (${precision}).`;
      } else {
        this.scaleTooltip = '';
      }
    },
    validateDecimalsToDisplay() {
      const {DecimalsToDisplay, Scale} = this.tableField.dataTypeJSON;
      const decimalsToDisplay = Number(DecimalsToDisplay);
      const scale = Number(Scale);
      this.decimalsToDisplayError = decimalsToDisplay < 0 || decimalsToDisplay > scale;
      if (this.decimalsToDisplayError) {
        this.showDecimalsTooltip();
      } else {
        this.decimalsTooltip = '';
      }
    },
    showDecimalsTooltip() {
      const {DecimalsToDisplay, Scale} = this.tableField.dataTypeJSON;
      const decimalsToDisplay = Number(DecimalsToDisplay);
      const scale = Number(Scale);
      if (decimalsToDisplay < 0) {
        this.decimalsTooltip = 'Decimals to Display cannot be less than 0.';
      } else if (decimalsToDisplay > scale) {
        this.decimalsTooltip = `Decimals to Display must not exceed Scale (${scale}).`;
      } else {
        this.decimalsTooltip = '';
      }
    },
    async onSharePointLocationSelected(locationData) {
      // Update the tableField dataTypeJSON with the selected SharePoint location
      const { type, id, siteId, driveId, parentReference } = locationData.item;
      const data = {};

      if (type === 'site') {
        data.SPDefaultSiteId = id;
        data.SPDefaultDriveId = '';
        data.SPDefaultFolderId = '';
        data.SPDefaultFolderPath = '';
      } else if (type === 'drive') {
        data.SPDefaultSiteId = siteId;
        data.SPDefaultDriveId = id;
        data.SPDefaultFolderId = '';
        data.SPDefaultFolderPath = '';
      } else if (type === 'folder') {
        data.SPDefaultSiteId = siteId;
        data.SPDefaultDriveId = driveId;
        data.SPDefaultFolderId = id;
        data.SPDefaultFolderPath = parentReference?.path;
      }

      for (const key in data) {
        this.$set(this.tableField.dataTypeJSON, key, data[key]);
      }
      this.showSharePointModal = false;
      await this.getSharePointDisplayPath();
    },
    async getSharePointDisplayPath() {
      this.sharePointDisplayPathError = '';
      try {
        this.sharePointDisplayPath = await constructSharePointDisplayPath(
          this.tableField.dataTypeJSON.SPDefaultSiteId,
          this.tableField.dataTypeJSON.SPDefaultDriveId,
          this.tableField.dataTypeJSON.SPDefaultFolderId,
          this.tableField.dataTypeJSON.SPDefaultFolderPath,
        );
      } catch (err) {
        if (err?.response?.status === 403 || /access denied/i.test(err?.message)) {
          this.sharePointDisplayPathError = 'You do not have permission to access the default SharePoint location.';
        } else {
          this.sharePointDisplayPathError = 'Failed to load default SharePoint location.';
        }
        this.sharePointDisplayPath = '';
      }
    },
  },
  created() {
    this.ffs = localStorage.getItem('featureFlags');
  },
  async beforeMount() {
    this.loading = true;
    await this.getFields();
    await this.getTables();
    if (this.fieldTypeIsDocument) await this.getSharePointDisplayPath();
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
$btn-delete-option-width: 60px;

.options-card {
  fieldset {
    width: 100%;
  }
}

.add-option-wrapper {
  padding-right: $btn-delete-option-width - 20px;
}

.btn-options-collapse {
  .fa-chevron-down {
    transform: rotate(180deg);
    transition: all 0.3s;
  }

  .btn-delete-option {
    width: $btn-delete-option-width;
  }

  &.collapsed {
    .fa-chevron-down {
      transform: rotate(0deg);
    }
  }
}

.lookup-configuration__wrapper,
.inline__label {
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    margin-bottom: 0;
  }
}

.grid-item {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;

  &:first-child {
    margin-bottom: 10px;
  }

  //align-items: baseline;
}

.selected-field-ai-calculation {
  background: #0F355E;
  color: white;
}

.fields-ai-calc-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.fields-ai-calc-box {
  border: 1px solid lightgray;
  padding: 5px;
  margin-bottom: 5px;
  border-radius: 5px;
}
</style>
